import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";

import { AuthService } from "@core/services/auth.service";
import { CookieService } from "@core/services/cookie.service";
import { LoggerService } from "@core/services/logger.service";
import { UserPreferencesService } from "@core/services/user-preferences.service";
import { LoginRoutesConstants } from "@login/routes/login.routes.constants";
import { ModalService } from "@shared/components/modal/modal.service";

export const authGuard = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> => {
  const authService = inject(AuthService);
  const cookieService = inject(CookieService);
  const modalService = inject(ModalService);
  const router = inject(Router);
  const logger = inject(LoggerService);
  const userPreferencesService = inject(UserPreferencesService);

  try {
    if (!authService.isLoggedIn()) {
      logger.debug("No user in memory, trying to initialize auth...");
      const initialized = await authService.initializeAuth();

      if (!initialized) {
        logger.debug("Auth initialization failed");

        // Check if user has already had a temp account
        const hadTempUser = cookieService.getCookie("had_temp_user");

        if (hadTempUser === "true") {
          // User already had temp account, redirect to login
          logger.debug("User already had temp account, redirecting to login");
          await router.navigate([LoginRoutesConstants.main()]);
          return false;
        }

        // First-time visitor, try to create guest user
        logger.debug("First-time visitor, attempting to create guest user");
        const guestCreated = await authService.ensureGuest();

        if (!guestCreated) {
          // Guest creation failed (disabled or limit reached), redirect to login
          logger.debug("Guest creation failed, redirecting to login");
          await router.navigate([LoginRoutesConstants.main()]);
          return false;
        }

        // Guest user created successfully, show welcome modal
        logger.debug("Guest user created successfully, showing welcome modal");
        modalService.openWelcomeModal(false);
      }
    }

    const isNewUser = route.queryParams["isNewUser"];
    if (isNewUser) {
      userPreferencesService.setIsNewUser(true);
    }

    return true;
  } catch (error: any) {
    logger.error("Unexpected error in auth guard:", error);
    await router.navigate([LoginRoutesConstants.main()]);
    return false;
  }
};
